import { useTranslation } from 'react-i18next';
import { Download, FileText, Music2 } from 'lucide-react';
import { Button, EmptyState } from '@ntconsult/aluma-react';
import { formatFileSize } from '@common/utils/format';
import { inferFileKind, type FileKind } from '@common/utils/fileKind';

interface SharedFileViewerProps {
  name: string;
  size: number;
  mimeType: string;
  streamUrl: string;
  onDownload: () => void;
}

export function SharedFileViewer({
  name,
  size,
  mimeType,
  streamUrl,
  onDownload,
}: SharedFileViewerProps) {
  const { t } = useTranslation('shares');
  const kind = inferFileKind(mimeType, name);
  const wide = kind === 'pdf';

  return (
    <div className={wide ? 'w-full max-w-5xl' : 'w-full max-w-3xl'}>
      <header className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <h1 className="text-lg font-bold text-foreground break-words leading-tight">
            {name}
          </h1>
          <p className="text-xs text-foreground-muted mt-0.5">
            {formatFileSize(size)}
          </p>
        </div>
        <Button
          onClick={onDownload}
          variant="secondary"
          size="sm"
          leftIcon={<Download className="w-4 h-4" aria-hidden="true" />}
        >
          {t('public.download')}
        </Button>
      </header>

      <Preview kind={kind} name={name} streamUrl={streamUrl} t={t} />
    </div>
  );
}

interface PreviewProps {
  kind: FileKind;
  name: string;
  streamUrl: string;
  t: (key: string) => string;
}

function Preview({ kind, name, streamUrl, t }: PreviewProps) {
  if (kind === 'video') {
    return (
      <video
        src={streamUrl}
        controls
        controlsList="nodownload"
        className="w-full aspect-video bg-black rounded-lg"
      />
    );
  }

  if (kind === 'audio') {
    return (
      <div className="border border-border rounded-lg p-6 flex items-center gap-4">
        <Music2
          className="w-8 h-8 text-foreground-muted shrink-0"
          aria-hidden="true"
        />
        <audio src={streamUrl} controls className="w-full" />
      </div>
    );
  }

  if (kind === 'image') {
    return (
      <img
        src={streamUrl}
        alt={name}
        className="max-w-full max-h-[70vh] mx-auto rounded-lg"
      />
    );
  }

  if (kind === 'pdf') {
    return (
      <iframe
        src={streamUrl}
        title={name}
        className="w-full h-[75vh] border border-border rounded-lg bg-white"
      />
    );
  }

  return (
    <EmptyState
      decorator={
        <FileText
          className="w-8 h-8 text-foreground-muted"
          aria-hidden="true"
        />
      }
      decoratorPlacement="top"
      align="center"
      card
      title={t('public.previewUnavailable')}
      description={t('public.previewUnavailableHint')}
    />
  );
}
